import { useCallback, useMemo } from 'react';
import { SelectMenuProps } from 'src/components/SelectMenu';
import { SIDE_PANEL_ROW_CONTENT_HEIGHT } from 'src/components/SidePanelRow';
import {
  CustomAttributeOption,
  CustomAttributeValue,
} from 'src/graphql/entities/custom-attributes/custom-attributes.types';
import { CustomAttributeEditProps } from 'src/graphql/entities/custom-attributes/field-view-registry/types';
import { toSelectMenuOptions } from 'src/graphql/entities/custom-attributes/field-view-registry/utils';

/**
 * Everything the single- and multi-select attribute editors present the same
 * way. Only the value shape differs between them, so the menu's own
 * configuration lives here rather than being restated per editor.
 */
export const ATTRIBUTE_SELECT_MENU_PROPS = {
  search: true,
  emptyText: 'No options found.',
  variant: 'inline',
} satisfies Partial<SelectMenuProps>;

/** For editors that keep the menu's default trigger as the row's value control. */
export const ATTRIBUTE_SELECT_TRIGGER_PROPS = {
  clearable: true,
  // Fills the row's content height so the trigger's hover surface matches the one a
  // built-in property row draws, rather than standing a few pixels taller.
  sx: { width: '100%', minHeight: SIDE_PANEL_ROW_CONTENT_HEIGHT },
} satisfies Partial<SelectMenuProps>;

export const useAttributeMenuOptions = (options: CustomAttributeOption[]) =>
  useMemo(() => toSelectMenuOptions(options), [options]);

/** Writes the edited value through the row, which owns the debounce and the save. */
export const useAttributeCommit = ({
  onChange,
  onSave,
}: Pick<CustomAttributeEditProps, 'onChange' | 'onSave'>) =>
  useCallback(
    (value: CustomAttributeValue) => {
      onChange(value);
      onSave(value);
    },
    [onChange, onSave],
  );
